/**
 * Analytics configuration constants — shared between frontend and (potentially) backend.
 *
 * These constants determine which users, environments, and clients are EXCLUDED
 * from anonymous pageview tracking. See frontend/src/hooks/usePageTracking.ts for
 * the consumer.
 */

/**
 * Emails to exclude from analytics tracking.
 *
 * Use this for test/QA/support accounts that should not pollute analytics data.
 * This is a frontend-only check — the value is shipped in the bundle.
 *
 * For per-user control without a redeploy, also set the Cognito custom attribute
 * `custom:excludeFromAnalytics = 'true'` on the user account.
 *
 * Compared case-insensitively.
 */
export const EXCLUDED_EMAILS: readonly string[] = [
  // Populate with your test/QA emails, e.g.:
  'alanblackburn66@gmail.com',
  'alan@tabletryb.com',
];

/**
 * Hostnames where analytics tracking should NEVER fire.
 *
 * Exact-match hostnames (plus `*.amplifyapp.com` handled by isExcludedHostname).
 */
export const EXCLUDED_HOSTNAMES: readonly string[] = [
  'localhost',
  '127.0.0.1',
];

/**
 * User-agent patterns that identify bots/crawlers.
 *
 * Matched case-insensitively via the `i` flag on each pattern.
 */
export const BOT_USER_AGENT_PATTERNS: readonly RegExp[] = [
  /bot/i,
  /crawler/i,
  /spider/i,
  /headless/i,
  /Googlebot/i,
  /Bingbot/i,
  /Slurp/i,
  /DuckDuckBot/i,
  /Baiduspider/i,
  /YandexBot/i,
  /facebookexternalhit/i,
  /Twitterbot/i,
  /LinkedInBot/i,
  /WhatsApp/i,
  /Slackbot/i,
  /TelegramBot/i,
  /Discordbot/i,
];

/**
 * Returns true if the given hostname should be excluded from analytics.
 * Handles exact matches from EXCLUDED_HOSTNAMES plus Amplify preview URLs.
 */
export function isExcludedHostname(hostname: string): boolean {
  if (EXCLUDED_HOSTNAMES.includes(hostname)) return true;
  if (hostname.endsWith('.amplifyapp.com')) return true;
  return false;
}

/**
 * Returns true if the given user-agent string appears to be a bot.
 */
export function isBotUserAgent(userAgent: string): boolean {
  if (!userAgent) return false;
  return BOT_USER_AGENT_PATTERNS.some((pattern) => pattern.test(userAgent));
}

/**
 * Returns true if the given email should be excluded from analytics.
 * Compared case-insensitively.
 */
export function isExcludedEmail(email: string | undefined | null): boolean {
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  return EXCLUDED_EMAILS.some((e) => e.toLowerCase() === normalized);
}
